import { useState } from "react";
import { CheckCircle, XCircle, CircleNotch, Play } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { fmtLatency } from "@/lib/format";
import { btnGhost } from "@/lib/ui";

const TESTS = [
  { id: "transcription", label: "Transcription", sub: "5s test clip through Gemini", path: "/admin/test/transcription" },
  { id: "transcription-scribe", label: "Transcription · Scribe", sub: "test clip, voices separated + named", path: "/admin/test/transcription/scribe" },
  { id: "transcription-35", label: "Transcription · 3.5 Transcribe", sub: "test clip, speaker labels on", path: "/admin/test/transcription/gemini35" },
  { id: "transcription-sarvam", label: "Transcription fallback", sub: "same clip through Sarvam", path: "/admin/test/transcription/sarvam" },
  { id: "anthropic", label: "Reasoning · Anthropic", sub: "one round-trip", path: "/admin/test/reasoning/anthropic" },
  { id: "fireworks", label: "Reasoning · Fireworks", sub: "one round-trip", path: "/admin/test/reasoning/fireworks" },
  { id: "flag", label: "Flag check", sub: "Fireworks, falls back to Anthropic", path: "/admin/test/flag" },
  { id: "tts-elevenlabs", label: "Read aloud · ElevenLabs", sub: "one short sentence", path: "/admin/test/tts/elevenlabs" },
  { id: "tts-sarvam", label: "Read aloud · Sarvam", sub: "one short sentence", path: "/admin/test/tts/sarvam" },
];

const Status = ({ r }) => {
  if (!r) return <span className="font-mono text-xs text-[var(--o-muted3)]">not run</span>;
  if (r.running) return <CircleNotch size={16} className="animate-spin text-[var(--o-muted)]" />;
  return r.ok ? (
    <span className="flex items-center gap-1.5 font-mono text-xs text-[#34D399]"><CheckCircle size={16} weight="fill" /> PASS</span>
  ) : (
    <span className="flex items-center gap-1.5 font-mono text-xs text-[#EF4444]"><XCircle size={16} weight="fill" /> FAIL</span>
  );
};

export const TestsPanel = () => {
  const [res, setRes] = useState({});
  const run = async (t) => {
    setRes((p) => ({ ...p, [t.id]: { running: true } }));
    try {
      const { data } = await api.post(t.path, {}, { timeout: 120000 });
      setRes((p) => ({ ...p, [t.id]: data }));
    } catch (e) {
      setRes((p) => ({ ...p, [t.id]: { ok: false, error: errMsg(e) } }));
    }
  };
  const runAll = () => TESTS.forEach(run);

  return (
    <div className="border border-[var(--o-border)]" data-testid="tests-panel">
      <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--o-border)]">
        <div>
          <h2 className="font-display text-base md:text-lg font-bold">Connection tests</h2>
          <p className="text-xs text-[var(--o-muted2)] mt-1">Run before every meeting. A dead key mid-meeting kills the experiment.</p>
        </div>
        <button onClick={runAll} className={btnGhost} data-testid="test-run-all-button"><Play size={14} weight="fill" /> Run all</button>
      </div>
      {TESTS.map((t) => {
        const r = res[t.id];
        return (
          <div key={t.id} className="grid grid-cols-[1fr_auto] sm:grid-cols-[220px_90px_80px_1fr_auto] gap-3 items-center px-5 py-3.5 border-b border-[var(--o-border-soft)] last:border-b-0" data-testid={`test-row-${t.id}`}>
            <div>
              <div className="text-sm font-semibold">{t.label}</div>
              <div className="text-xs text-[var(--o-muted3)]">{t.sub}</div>
            </div>
            <div data-testid={`test-status-${t.id}`}><Status r={r} /></div>
            <span className="font-mono text-xs text-[var(--o-muted)] hidden sm:block" data-testid={`test-latency-${t.id}`}>{r && !r.running ? fmtLatency(r.latency_ms) : ""}</span>
            <p className="font-mono text-[11px] text-[var(--o-muted2)] truncate col-span-2 sm:col-span-1" title={r?.error || r?.output} data-testid={`test-output-${t.id}`}>
              {r && !r.running ? (r.ok ? `${r.provider ? `${r.provider} · ` : ""}${r.model}${r.key_source ? ` · ${r.key_source} key` : ""} — ${r.output}` : r.error) : ""}
            </p>
            <button onClick={() => run(t)} disabled={r?.running} className="font-mono text-xs border border-[var(--o-border)] px-3 py-1.5 hover:bg-[var(--o-surface)] disabled:opacity-40 transition-colors" data-testid={`test-run-${t.id}`}>Test</button>
          </div>
        );
      })}
    </div>
  );
};
